// src/leaveRequestsAdmin/index.js -> dist/leaveRequestsAdmin.js (globalName: LeaveRequestsAdmin)
//
// Leave requests, the ADMIN side (docs/leave-requests-plan.md; Matt approved the 11 decisions on 17 Oct 2026). It
// replaces the placeholder board that only ever changed a page variable. Renders into #reqList on People, Leave
// requests. The server decides everything that matters (src/worker/routes/leave-requests.js): who may approve
// (area manager for their own area, Head Office for anything; APL Head Office only; never your own), the day-by-day
// plan, the rota write and its checks. This page shows the plan and sends the approver's choices.
//
// PAGE-GLOBAL STATE (declared in admin.html, never here): requestsData. It is filled IN PLACE from the server's list
// (never rebound), so the dashboard tile (src/adminPanels), the menu badge (src/uploads/staff.js) and the AI rota
// prompt (src/uploads/rota.js) read the real pending requests. Bare-name globals from the admin.html inline core
// (DB, escapeHtml, showToast, getVisibleStaff, populateReqStaffSelect, renderDashboard) are typeof-guarded.
//
// Every handler a button calls is EXPORTED (the esbuild globalName clobber lesson). Arabic: every phrase sits in its
// own text node so the translator can find it; names, stores and notes are translate="no". Logic never reads text
// off the screen: it reads ids, values and data.

import { PROPERTY_ITEMS, EMERGENCY_PROOF_TEXT, isPhoneLike, needsContacts, propertyText } from '../shared/leave-form.js';

var TYPE_LABELS = { holiday: 'Holiday', toil: 'TOIL', apl: 'Authorised paid leave' };
var TYPE_ICONS = { holiday: '🏖', toil: '⏳', apl: '📋' };
var APL_LABELS = { maternity: 'Maternity', paternity: 'Paternity', compassionate: 'Compassionate', other: 'Other' };
var STATUS = {
  pending: { label: 'Waiting', cls: 'badge-amber' },
  approved: { label: 'Approved', cls: 'badge-green' },
  declined: { label: 'Declined', cls: 'badge-red' },
  withdrawn: { label: 'Withdrawn', cls: 'badge-red' },
  cancelled: { label: 'Cancelled', cls: 'badge-red' },
};
// 19 Oct 2026 (Matt, option A): two steps. The area manager gives an initial OK ("OK and send to HR", which books
// nothing), then HR (a Head Office account) gives the FINAL approval, which books the days. A request waiting for the
// OK is stage 'needs_ok'; one with the OK, waiting for HR, is 'with_hr' (the row's status is still 'pending'; the
// server derives the stage from the OK it recorded).
var STAGE = {
  needs_ok: { label: 'Needs the area manager\'s OK', cls: 'badge-amber' },
  with_hr: { label: 'Waiting for HR', cls: 'badge-amber' },
};
export function stageOf(r) {
  if (!r) return null;
  if (r.stage) return r.stage;
  if (r.status !== 'pending') return r.status;
  var ok = false;
  (Array.isArray(r.booked_days) ? r.booked_days : []).forEach(function (b) { if (b && b.action === 'am_ok') ok = true; });
  return ok || r.am_ok ? 'with_hr' : 'needs_ok';
}
var ACTION_LABELS = { book: 'Book', same: 'Already booked', off: 'Day off', unticked: 'Unticked', refused: 'Cannot book' };

var _rows = [];              // the server's list
var _state = { enabled: null, error: '', canApl: false, filterSet: false };
var _preview = {};           // request id -> { data, untick: [dates], ack: bool }
var _loading = false;

function esc(s) { return (typeof escapeHtml === 'function') ? escapeHtml(String(s == null ? '' : s)) : String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return '&#' + c.charCodeAt(0) + ';'; }); }
function toast(msg, kind) { if (typeof showToast === 'function') showToast(msg, kind); }
function tr(s) { return (typeof I18N !== 'undefined' && I18N.t) ? I18N.t(s) : s; }
function dates(r) { return r.start_date === r.end_date ? r.start_date : r.start_date + ' to ' + r.end_date; }
function dayCount(a, b) {
  var d1 = Date.parse(a + 'T12:00:00Z'), d2 = Date.parse(b + 'T12:00:00Z');
  return (isNaN(d1) || isNaN(d2)) ? 0 : Math.round((d2 - d1) / 86400000) + 1;
}
function daysText(n) { return n === 1 ? '1 day' : n + ' days'; }

// The pending requests in the shape the older readers expect ({ staffName, store, type, date, reason, status }),
// plus the full record. The note is NOT passed on as the reason: the AI rota prompt reads that field.
function legacyShape(r) {
  return {
    id: r.id, staffId: r.staff_id, staffName: r.staff_name || ('Staff #' + r.staff_id), store: r.store,
    type: 'leave request (' + (TYPE_LABELS[r.leave_type] || r.leave_type) + ')', date: dates(r), reason: '',
    status: r.status, leave: r,
  };
}
function syncShared() {
  if (typeof requestsData !== 'undefined' && Array.isArray(requestsData)) {
    requestsData.splice.apply(requestsData, [0, requestsData.length].concat(_rows.map(legacyShape)));
  }
  // The count is what THIS account can act on: Head Office (HR) the requests waiting for HR, an area manager the
  // requests waiting for their OK.
  var mine = _state.canApl ? 'with_hr' : 'needs_ok';
  var pending = _rows.filter(function (r) { return stageOf(r) === mine; }).length;
  var tile = document.getElementById('dPendingReqs');
  if (tile) tile.textContent = String(pending);
  var badge = document.getElementById('reqBadge');
  if (badge) { badge.textContent = String(pending); badge.style.display = pending ? '' : 'none'; }
}

async function load() {
  if (typeof DB === 'undefined' || !DB.leaveRequests) return;
  _loading = true;
  try {
    var res = await DB.leaveRequests.adminList();
    _state.enabled = !!(res && res.enabled);
    _state.canApl = !!(res && res.can_decide_apl);
    _state.error = '';
    _rows = (res && Array.isArray(res.requests)) ? res.requests : [];
  } catch (e) {
    _state.error = (e && e.message) || 'Could not load leave requests.';
    _rows = [];
  } finally {
    _loading = false;
  }
  syncShared();
}

// The page. Called by People, Leave requests (and by the dashboard at sign-in, through renderRequests).
async function render() {
  var list = document.getElementById('reqList');
  if (!list) { await load(); return; }
  if (!_rows.length && !_state.error) list.innerHTML = '<div class="card" style="padding:20px;color:var(--sub)"><span>Loading leave requests...</span></div>';
  await load();
  paint();
}

function paint() {
  var list = document.getElementById('reqList');
  if (!list) return;
  if (_state.error) {
    list.innerHTML = '<div class="card" style="padding:20px;color:var(--red)"><span>' + esc(_state.error) + '</span></div>';
    return;
  }
  if (_state.enabled === false) {
    list.innerHTML = '<div class="card" style="text-align:center;padding:30px;color:var(--sub)"><div style="font-size:30px;margin-bottom:6px">🔒</div>' +
      '<div><span>Leave requests are switched off.</span></div><div style="margin-top:4px"><span>Head Office turns them on in admin, App status.</span></div></div>';
    return;
  }
  var sel = document.getElementById('reqFilterStatus');
  // The first time: open on what this account acts on ("Waiting for HR" for Head Office, "Needs your OK" otherwise).
  if (sel && !_state.filterSet) {
    _state.filterSet = true;
    var want = _state.canApl ? 'with_hr' : 'needs_ok';
    if (sel.querySelector('option[value="' + want + '"]') && sel.value === 'pending') sel.value = want;
  }
  var f = sel ? sel.value : 'pending';
  var rows = f ? _rows.filter(function (r) { return f === 'needs_ok' || f === 'with_hr' ? stageOf(r) === f : r.status === f; }) : _rows.slice();
  rows.sort(function (a, b) { return a.status === b.status ? (a.start_date < b.start_date ? -1 : 1) : (a.status === 'pending' ? -1 : 1); });
  if (!rows.length) {
    list.innerHTML = '<div class="card" style="text-align:center;padding:40px;color:var(--sub)"><div style="font-size:36px;margin-bottom:8px">✅</div><span>No leave requests here</span></div>';
    return;
  }
  list.innerHTML = rows.map(cardHtml).join('');
  Object.keys(_preview).forEach(function (id) { paintPreview(id); });
}

function cardHtml(r) {
  var stage = stageOf(r);
  var st = STAGE[stage] || STATUS[r.status] || { label: r.status, cls: 'badge-amber' };
  var n = dayCount(r.start_date, r.end_date);
  var h = '<div class="req-card" data-lr="' + r.id + '">' +
    '<div class="req-icon">' + (TYPE_ICONS[r.leave_type] || '📋') + '</div>' +
    '<div class="req-info">' +
      '<div class="req-name"><span translate="no">' + esc(r.staff_name || ('Staff #' + r.staff_id)) + '</span> <span class="badge ' + st.cls + '">' + esc(st.label) + '</span> <span class="badge" translate="no">' + esc(r.ticket || ('LR-' + r.id)) + '</span></div>' +
      '<div class="req-detail"><span translate="no">' + esc(r.store) + '</span> · <span>' + esc(TYPE_LABELS[r.leave_type] || r.leave_type) + '</span> · <span>' + esc(dates(r)) + '</span> · <span>' + esc(daysText(n)) + '</span></div>';
  if (r.leave_type === 'apl' && r.apl_reason) h += '<div class="req-detail"><span>Reason for the paid leave:</span> <span>' + esc(APL_LABELS[r.apl_reason] || r.apl_reason) + '</span></div>';
  if (r.leave_type === 'toil' && r.toil_hours != null) h += '<div class="req-detail"><span>' + esc(r.toil_hours + ' hours of TOIL') + '</span></div>';
  if (r.form) h += formDetailsHtml(r);   // 19 Oct 2026: the Leave Application Form's fields (reason included)
  else if (r.note) h += '<div class="req-reason" translate="no">"' + esc(r.note) + '"</div>';   // an older request's note
  h += noticeHtml(r.notice, r.leave_type);   // 19 Oct 2026: the Holiday Policy's notice and decide-by date
  h += '<div class="req-detail"><span>' + (r.requested_via === 'admin' ? 'Raised in admin by' : 'Asked on the till by') + '</span> <span translate="no">' + esc(r.requested_by_name || '-') + '</span></div>';
  if (r.am_ok && r.am_ok.by) {   // 19 Oct 2026: the area manager's initial OK
    h += '<div class="req-detail"><span>OK\'d by</span> <span translate="no">' + esc(r.am_ok.by) + '</span>' + (r.am_ok.at ? stampHtml(r.am_ok.at) : '') +
      (r.am_ok.overdraw_ack ? ' · <span>past the balance</span>' : '') + '</div>';
    if (r.am_ok.comment) h += '<div class="req-reason" translate="no">"' + esc(r.am_ok.comment) + '"</div>';
  }
  if (r.decided_by_name) {
    h += '<div class="req-detail"><span>' + (r.status === 'declined' ? 'Declined by' : 'Decided by') + '</span> <span translate="no">' + esc(r.decided_by_name) + '</span>' +
      (r.overdraw_ack ? ' · <span>approved past the balance</span>' : '') + '</div>';
  }
  if (r.decision_comment) h += '<div class="req-reason" translate="no">"' + esc(r.decision_comment) + '"</div>';
  if (r.status === 'approved' && Array.isArray(r.booked_days)) {
    var b = r.booked_days.filter(function (d) { return d && d.action === 'booked'; }).length;
    h += '<div class="req-detail"><span>' + esc('On the rota: ' + daysText(b)) + '</span></div>';
  }
  if (r.cancelled_by_name) h += '<div class="req-detail"><span>Cancelled by</span> <span translate="no">' + esc(r.cancelled_by_name) + '</span></div>';
  h += hrHtml(r);   // 19 Oct 2026: did HR get it (for Amanah)?
  if (stage === 'with_hr' && !_state.canApl) {
    h += '<div class="req-detail"><span>Waiting for HR to approve. Only Head Office (HR) can approve or decline it now.</span></div>';
  } else if (r.status === 'pending') {
    var hrStep = stage === 'with_hr';
    h += '<div class="req-actions">' +
      '<button type="button" class="req-btn req-approve" onclick="LeaveRequestsAdmin.openPreview(' + r.id + ')">' + (hrStep ? 'Check and approve (HR)' : 'Check and OK') + '</button>' +
      '<button type="button" class="req-btn req-decline" onclick="LeaveRequestsAdmin.startDecline(' + r.id + ')">' + (hrStep ? 'Decline (HR)' : 'Decline') + '</button>' +
      '</div>' +
      '<div id="lrDecline-' + r.id + '" style="display:none;margin-top:8px">' +
        '<textarea id="lrDeclineText-' + r.id + '" class="form-input" rows="2" maxlength="500" placeholder="Why is it declined? The person is told." style="width:100%;box-sizing:border-box"></textarea>' +
        '<div class="req-actions"><button type="button" class="req-btn req-decline" onclick="LeaveRequestsAdmin.confirmDecline(' + r.id + ')">Decline with this reason</button>' +
        '<button type="button" class="req-btn" onclick="LeaveRequestsAdmin.startDecline(' + r.id + ')">Keep it</button></div>' +
      '</div>';
  }
  if (r.status === 'approved') {
    h += '<div class="req-actions"><button type="button" class="req-btn req-decline" onclick="LeaveRequestsAdmin.cancelLeave(' + r.id + ')">Cancel this leave</button></div>';
  }
  h += '<div id="lrPreview-' + r.id + '"></div>';
  return h + '</div></div>';
}

// ── The Leave Application Form's fields (Matt, 19 Oct 2026; src/shared/leave-form.js) ──
// Private: shown here and sent to HR, never on the till. Numbers, the reason and the property text are what people
// typed, so they are translate="no".
export function formDetailsHtml(r) {
  var f = (r && r.form) || null;
  if (!f) return '';
  var h = '';
  if (r.leave_type === 'holiday') {
    h += '<div class="req-detail"><b><span>' + (f.leave_kind === 'emergency' ? 'Emergency leave' : 'Annual leave') + '</span></b>' +
      (f.leave_kind !== 'emergency' && f.ticket ? ' · <span>Air ticket requested</span>' : '') + '</div>';
    if (f.leave_kind === 'emergency') h += '<div class="req-detail" style="color:var(--red)"><span>' + esc(EMERGENCY_PROOF_TEXT) + '</span></div>';
  }
  h += '<div class="req-detail"><span>Local contact:</span> <span translate="no">' + esc(f.contact_local || '-') + '</span>' +
    ' · <span>Home-town contact:</span> <span translate="no">' + esc(f.contact_home || '-') + '</span></div>';
  if (r.note) h += '<div class="req-detail"><span>Reason for leave:</span></div><div class="req-reason" translate="no">"' + esc(r.note) + '"</div>';
  var props = Array.isArray(f.property) ? f.property : [];
  h += '<div class="req-detail"><span>Company property to return:</span> ' + (props.length
    ? '<span translate="no">' + esc(propertyText(f)) + '</span>' : '<span>None</span>') + '</div>';
  return h;
}

// ── Notice (Matt, 19 Oct 2026; ALGT's Holiday Policy) ──
// The server works it out (noticeAndDecideBy in src/worker/leave-requests.js): the days from when the request was
// raised to its first day, and the date it should be decided by (1 week after it was raised for up to 7 days of
// leave, 2 weeks for 8 to 14, 3 weeks for longer). Under four weeks' notice on a HOLIDAY request the approver sees
// a warning; it never blocks. (TOIL and authorised paid leave, which covers compassionate leave, show the notice
// without the warning.)
export var CALENDAR_RULE_TEXT = 'Annual leave counts every calendar day, days off included, as in the Holiday Policy and Amanah.';
export var TOIL_RULE_TEXT = 'TOIL skips weekly days off: it repays hours worked.';
export var SHORT_NOTICE_TEXT ='Less than four weeks\' notice (the Holiday Policy asks for four)';
export function noticeHtml(n, leaveType) {
  if (!n || n.notice_days == null) return '';
  var h = '<div class="req-detail"><span>Notice given:</span> <span>' + esc(daysText(Number(n.notice_days))) + '</span>' +
    (n.decide_by ? ' · <span>Decide by</span> ' + esc(n.decide_by) : '') + '</div>';
  if (n.short_notice && leaveType === 'holiday') {
    h += '<div class="req-detail" style="color:var(--red);font-weight:700"><span>' + esc(SHORT_NOTICE_TEXT) + '</span></div>';
  }
  return h;
}

// ── HR (19 Oct 2026; src/worker/leave-hr.js) ──
// Approving emails HR (Head Office's list in Automated emails) and the approver, so HR can key the days into Amanah;
// cancelling emails the days removed. The Worker records what happened as an entry with action 'hr' in booked_days,
// one per kind ('approved', 'cancelled'). This shows it, and offers "Send to HR again" to Head Office and the approver
// (hr_can_resend, decided by the Worker).
var MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// An ISO time as Dubai wall time, '19 Oct 10:42' in parts (UTC+4, no daylight saving). null if unparseable.
export function dubaiParts(iso) {
  var t = Date.parse(String(iso || ''));
  if (isNaN(t)) return null;
  var d = new Date(t + 4 * 3600 * 1000);
  var hh = String(d.getUTCHours()).padStart(2, '0'), mm = String(d.getUTCMinutes()).padStart(2, '0');
  return { day: String(d.getUTCDate()), mon: MON[d.getUTCMonth()], time: hh + ':' + mm };
}
function stampHtml(iso) {
  var p = dubaiParts(iso);
  return p ? ' ' + esc(p.day) + ' <span>' + esc(p.mon) + '</span> ' + esc(p.time) : '';
}
// Pure: what the HR line says for one request. -> { state, text, error, at } or null (nothing to say).
//   state: sent | no_hr | failed | skipped | not_sent
export function hrState(r) {
  var withHr = stageOf(r) === 'with_hr';   // 19 Oct 2026: the area manager's OK was emailed to HR (kind 'ok')
  if (!r || (!withHr && r.status !== 'approved' && r.status !== 'cancelled')) return null;
  var kind = withHr ? 'ok' : (r.status === 'cancelled' ? 'cancelled' : 'approved');
  var e = null;
  (Array.isArray(r.booked_days) ? r.booked_days : []).forEach(function (b) { if (b && b.action === 'hr' && b.kind === kind) e = b; });
  var cancelled = kind === 'cancelled';
  if (!e) return cancelled ? null : { state: 'not_sent', text: 'Not sent to HR yet', at: null };
  if (e.skipped) return { state: 'skipped', text: 'HR not emailed: no days were put on the rota', at: null };
  if (e.ok && e.hr_count > 0) return { state: 'sent', text: cancelled ? 'Cancellation sent to HR' : (withHr ? 'Sent to HR for approval' : 'Sent to HR'), at: e.at };
  if (!e.hr_count && (e.ok || /^No address/.test(String(e.error || '')))) {
    return { state: 'no_hr', text: 'HR not emailed: no HR address set in Automated emails', at: null };
  }
  return { state: 'failed', text: cancelled ? 'Cancellation email to HR failed:' : 'HR email failed:', error: String(e.error || 'unknown'), at: null };
}
export function hrHtml(r) {
  var s = hrState(r);
  if (!s) return '';
  var colour = s.state === 'sent' ? 'var(--sub)' : 'var(--red)';
  var h = '<div class="req-detail" style="color:' + colour + '"><span>' + esc(s.text) + '</span>' + (s.at ? stampHtml(s.at) : '') +
    (s.error ? ' <span translate="no">' + esc(s.error) + '</span>' : '') + '</div>';
  if (r.hr_can_resend && s.state !== 'skipped') {
    h += '<div class="req-actions"><button type="button" class="req-btn" id="lrHrBtn-' + r.id + '" onclick="LeaveRequestsAdmin.resendHr(' + r.id + ')">Send to HR again</button></div>';
  }
  return h;
}
// The toast after approving or cancelling, when HR did not get it. null when all is well.
export function hrToast(hr) {
  if (!hr) return null;
  if (hr.skipped) return null;
  if (!hr.hr_count && (hr.ok || /^No address/.test(String(hr.error || '')))) return 'HR not emailed: no HR address set in Automated emails';
  if (!hr.ok) return 'HR email failed: ' + String(hr.error || 'unknown');
  return null;
}
async function resendHr(id) {
  if (!window.confirm(tr('Send this to HR again?'))) return;
  var btn = document.getElementById('lrHrBtn-' + id);
  if (btn) btn.disabled = true;
  try {
    var res = await DB.leaveRequests.hrResend(id);
    var warn = hrToast(res && res.hr);
    if (warn) toast(warn, 'error'); else toast('Sent to HR', 'success');
  } catch (e) { toast((e && e.message) || 'Could not send to HR', 'error'); }
  await render();
}

// ── Decline ──
function startDecline(id) {
  var box = document.getElementById('lrDecline-' + id);
  if (!box) return;
  box.style.display = box.style.display === 'none' ? '' : 'none';
  var t = document.getElementById('lrDeclineText-' + id);
  if (t && box.style.display !== 'none') t.focus();
}
async function confirmDecline(id) {
  var t = document.getElementById('lrDeclineText-' + id);
  var why = t ? String(t.value || '').trim() : '';
  if (!why) { toast('Write the reason first. The person needs to know why.', 'error'); return; }
  try {
    await DB.leaveRequests.decline(id, why);
    toast('Leave request declined', 'success');
    delete _preview[id];
    await render();
  } catch (e) { toast((e && e.message) || 'Could not decline', 'error'); }
}

// ── Preview and approve ──
async function openPreview(id) {
  if (_preview[id] && _preview[id].data) { delete _preview[id]; paintPreview(id); return; }   // a second tap closes it
  _preview[id] = { data: null, untick: [], ack: false, busy: true };
  paintPreview(id);
  await refreshPreview(id);
}
async function refreshPreview(id) {
  var p = _preview[id];
  if (!p) return;
  p.busy = true;
  try {
    p.data = await DB.leaveRequests.preview(id, p.untick);
    p.error = '';
  } catch (e) { p.error = (e && e.message) || 'Could not work out the plan'; }
  p.busy = false;
  paintPreview(id);
}
function toggleDay(id, date) {
  var p = _preview[id];
  if (!p) return;
  var i = p.untick.indexOf(date);
  if (i === -1) p.untick.push(date); else p.untick.splice(i, 1);
  refreshPreview(id);
}
function toggleAck(id) {
  var p = _preview[id];
  if (!p) return;
  var box = document.getElementById('lrAck-' + id);
  p.ack = !!(box && box.checked);
}

function existingText(d) {
  if (!d.existing || !d.existing.length) return '<span>Nothing on the rota</span>';
  return d.existing.map(function (x) {
    var what = x.type === 'work' ? ((x.start_time || '') + '-' + (x.end_time || '')) : (TYPE_LABELS[x.type] || x.type);
    return '<span translate="no">' + esc(what) + '</span>' + (x.work_store ? ' <span translate="no">(' + esc(x.work_store) + ')</span>' : '');
  }).join(', ');
}

function paintPreview(id) {
  var box = document.getElementById('lrPreview-' + id);
  if (!box) return;
  var p = _preview[id];
  if (!p) { box.innerHTML = ''; return; }
  if (p.busy && !p.data) { box.innerHTML = '<div class="req-detail" style="margin-top:8px"><span>Working out the plan...</span></div>'; return; }
  if (p.error) { box.innerHTML = '<div class="req-detail" style="margin-top:8px;color:var(--red)"><span>' + esc(p.error) + '</span></div>'; return; }
  var d = p.data || {};
  var days = Array.isArray(d.days) ? d.days : [];
  var h = '<div style="margin-top:10px;border:1px solid var(--border);border-radius:10px;padding:10px;background:var(--surface-2)">' +
    '<div style="font-weight:700;font-size:13px;margin-bottom:6px"><span>Day by day</span></div>' +
    '<table style="width:100%;font-size:12px;border-collapse:collapse"><thead><tr>' +
      '<th style="text-align:left;padding:3px"><span>Book</span></th><th style="text-align:left;padding:3px"><span>Date</span></th>' +
      '<th style="text-align:left;padding:3px"><span>On the rota now</span></th><th style="text-align:left;padding:3px"><span>What happens</span></th></tr></thead><tbody>';
  days.forEach(function (day) {
    var tickable = d.stage !== 'with_hr' && (day.action === 'book' || day.action === 'unticked');   // HR books the area manager's ticks
    h += '<tr style="border-top:1px solid var(--border)">' +
      '<td style="padding:3px"><input type="checkbox" aria-label="Book this day"' + (day.action === 'book' ? ' checked' : '') + (tickable ? '' : ' disabled') +
        ' onchange="LeaveRequestsAdmin.toggleDay(' + id + ',\'' + esc(day.date) + '\')"></td>' +
      '<td style="padding:3px;white-space:nowrap"><span>' + esc(day.day) + '</span> ' + esc(day.date) + '</td>' +
      '<td style="padding:3px">' + existingText(day) + '</td>' +
      '<td style="padding:3px"><b><span>' + esc(ACTION_LABELS[day.action] || day.action) + '</span></b>' + (day.why ? ' <span>' + esc(day.why) + '</span>' : '') + '</td></tr>';
  });
  h += '</tbody></table>';
  // 19 Oct 2026 (Matt, "Follow the Amanah way"): holiday and APL count calendar days; TOIL skips days off.
  var rule = d.calendar_days ? CALENDAR_RULE_TEXT : (d.request && d.request.leave_type === 'toil' ? TOIL_RULE_TEXT : '');
  if (rule) h += '<div class="req-detail" style="margin-top:6px"><span>' + esc(rule) + '</span></div>';
  h += noticeHtml(d.notice, d.request && d.request.leave_type);
  (Array.isArray(d.balances) ? d.balances : []).forEach(function (b) {
    var line = d.request && d.request.leave_type === 'toil'
      ? 'TOIL ' + b.year + ': ' + b.toil_before + 'h now, ' + b.toil_after + 'h after'
      : (b.has_balance_row
        ? 'Holiday ' + b.year + ': ' + b.holiday_left_before + ' days left now, ' + b.holiday_left_after + ' after'
        : 'Holiday ' + b.year + ': no balance on file, ' + b.holiday_used_after + ' days booked after');
    if (d.request && d.request.leave_type === 'apl') line = 'Authorised paid leave uses no balance.';
    h += '<div class="req-detail" style="margin-top:6px"><span>' + esc(line) + '</span></div>';
  });
  var others = (Array.isArray(d.overlaps) ? d.overlaps : []);
  if (others.length) {
    h += '<div style="margin-top:6px;font-size:12px"><b><span>Also off in this store:</span></b> ' + others.map(function (o) {
      return '<span translate="no">' + esc(o.staff_name || ('Staff #' + o.staff_id)) + '</span> <span>' + esc(TYPE_LABELS[o.leave_type] || o.leave_type) + '</span> ' +
        esc(o.start_date === o.end_date ? o.start_date : o.start_date + ' to ' + o.end_date) + ' (<span>' + esc((STATUS[o.status] || {}).label || o.status) + '</span>)';
    }).join('; ') + '</div>';
  }
  var warnings = Array.isArray(d.warnings) ? d.warnings : [];
  if (warnings.length) {
    h += '<div style="margin-top:8px;padding:8px;border-radius:8px;background:var(--danger-bg);color:var(--danger-deep);font-size:12px">' +
      warnings.map(function (w) { return '<div><span>' + esc(w) + '</span></div>'; }).join('') +
      '<label style="display:flex;gap:6px;align-items:center;margin-top:6px;font-weight:700"><input type="checkbox" id="lrAck-' + id + '"' + (p.ack ? ' checked' : '') +
      ' onchange="LeaveRequestsAdmin.toggleAck(' + id + ')"><span>Approve anyway</span></label></div>';
  }
  var hrStep = d.stage === 'with_hr';
  if (hrStep) h += '<div class="req-detail" style="margin-top:6px"><span>HR approves the days the area manager ticked.</span></div>';
  if (d.can_decide === false) {
    h += '<div class="req-detail" style="margin-top:8px;color:var(--red)"><span>' + esc(d.self
      ? 'You cannot decide this request (it is your own).' : (hrStep ? 'Only Head Office (HR) gives the final approval.' : 'You cannot decide this request.')) + '</span></div>';
  } else if (hrStep) {
    // Step 2 (19 Oct 2026): HR's final approval books the days on the rota.
    h += '<input type="text" id="lrComment-' + id + '" class="form-input" maxlength="500" placeholder="A note for the record (optional)" style="width:100%;box-sizing:border-box;margin-top:8px">' +
      '<div class="req-actions"><button type="button" class="req-btn req-approve" id="lrApproveBtn-' + id + '" onclick="LeaveRequestsAdmin.approve(' + id + ')">Approve (HR) and put it on the rota</button></div>';
  } else {
    // Step 1: the area manager's OK books nothing; it sends the request to HR.
    h += '<input type="text" id="lrComment-' + id + '" class="form-input" maxlength="500" placeholder="A note for HR (optional)" style="width:100%;box-sizing:border-box;margin-top:8px">' +
      '<div class="req-actions"><button type="button" class="req-btn req-approve" id="lrApproveBtn-' + id + '" onclick="LeaveRequestsAdmin.okRequest(' + id + ')">OK and send to HR</button></div>' +
      '<div class="req-detail"><span>Nothing goes on the rota until HR approves.</span></div>';
  }
  box.innerHTML = h + '</div>';
}

// Step 1 (19 Oct 2026): the area manager's OK. Books nothing; the server records it and emails HR.
async function okRequest(id) {
  var p = _preview[id];
  if (!p || !p.data) return;
  var btn = document.getElementById('lrApproveBtn-' + id);
  if (btn) btn.disabled = true;
  var c = document.getElementById('lrComment-' + id);
  try {
    var res = await DB.leaveRequests.ok(id, { untick: p.untick.slice(), overdraw_ack: !!p.ack, comment: c ? c.value : '' });
    toast('OK sent to HR. Nothing is on the rota until HR approves.', 'success');
    var hrWarn = hrToast(res && res.hr);
    if (hrWarn) toast(hrWarn, 'error');
    delete _preview[id];
    await render();
  } catch (e) {
    toast((e && e.message) || 'Could not send the OK', 'error');
    if (btn) btn.disabled = false;
    refreshPreview(id);
  }
}

// Step 2: HR's final approval (Head Office). The server books the days the area manager ticked.
async function approve(id) {
  var p = _preview[id];
  if (!p || !p.data) return;
  var btn = document.getElementById('lrApproveBtn-' + id);
  if (btn) btn.disabled = true;
  var c = document.getElementById('lrComment-' + id);
  try {
    var res = await DB.leaveRequests.approve(id, { overdraw_ack: !!p.ack, comment: c ? c.value : '' });
    toast('Approved: ' + daysText(res && res.booked != null ? res.booked : 0) + ' on the rota', 'success');
    var w = [].concat(res.holiday_warnings || [], res.toil_warnings || [], res.overwrite_warnings || []);
    if (w.length) {
      toast(w.map(function (x) { return typeof x === 'string' ? x : (x.message || (x.name ? x.name + ': past the balance' : '')); }).filter(Boolean).join(' | '), 'error');
    }
    var hrWarn = hrToast(res && res.hr);   // 19 Oct 2026: the approval stands either way
    if (hrWarn) toast(hrWarn, 'error');
    delete _preview[id];
    await render();
  } catch (e) {
    toast((e && e.message) || 'Could not approve', 'error');
    if (btn) btn.disabled = false;
    refreshPreview(id);
  }
}

// ── Cancel approved leave ──
async function cancelLeave(id) {
  if (!window.confirm(tr('Cancel this leave? The days from tomorrow that are still exactly as approved come off the rota and need cover. Days already started, or changed on the rota since, stay as they are.'))) return;
  try {
    var res = await DB.leaveRequests.cancel(id);
    var need = (res && res.needs_cover) || [];
    toast(need.length ? ('Leave cancelled. Needs cover: ' + need.join(', ')) : 'Leave cancelled. No days came off the rota.', 'success');
    var hrWarn = hrToast(res && res.hr);   // 19 Oct 2026: the cancellation stands either way
    if (hrWarn) toast(hrWarn, 'error');
    await render();
  } catch (e) { toast((e && e.message) || 'Could not cancel', 'error'); }
}

// ── Raise one in admin ──
function syncRaiseFields() {
  var t = (document.getElementById('reqType') || {}).value;
  var show = function (id, on) { var el = document.getElementById(id); if (el) el.style.display = on ? '' : 'none'; };
  show('reqToilBox', t === 'toil');
  show('reqAplBox', t === 'apl');
  // 19 Oct 2026: the Leave Application Form's fields (src/shared/leave-form.js)
  var kind = (document.getElementById('reqKind') || {}).value;
  show('reqKindBox', t === 'holiday');
  show('reqTicketBox', t === 'holiday' && kind !== 'emergency');
  show('reqProofBox', t === 'holiday' && kind === 'emergency');
  var other = document.getElementById('reqProp-other');
  show('reqPropOther', !!(other && other.checked));
}
// The property tick list, drawn from the one list (src/shared/leave-form.js).
function paintPropertyBox() {
  var box = document.getElementById('reqPropBox');
  if (!box) return;
  box.innerHTML = PROPERTY_ITEMS.map(function (p) {
    return '<label style="display:inline-flex;gap:4px;align-items:center;margin:2px 10px 2px 0;font-size:13px"><input type="checkbox" id="reqProp-' + p.key + '"' +
      (p.key === 'other' ? ' onchange="LeaveRequestsAdmin.syncRaiseFields()"' : '') + '><span>' + esc(p.label) + '</span></label>';
  }).join('');
}
// The local contact number starts as the phone on the person's staff record (when admin has it); it can be changed.
function prefillLocal() {
  var sel = document.getElementById('reqStaff');
  var box = document.getElementById('reqLocal');
  if (!sel || !box) return;
  var list = (typeof getVisibleStaff === 'function') ? (getVisibleStaff() || []) : [];
  var s = null;
  for (var i = 0; i < list.length; i++) if (list[i] && String(list[i].id) === String(sel.value)) s = list[i];
  box.value = s && s.phone && isPhoneLike(s.phone) ? String(s.phone).trim() : '';
}
function openRaise() {
  if (typeof populateReqStaffSelect === 'function') { try { populateReqStaffSelect(); } catch (e) { /* the list stays as it was */ } }
  paintPropertyBox();
  var sel = document.getElementById('reqStaff');
  if (sel) sel.onchange = prefillLocal;
  prefillLocal();
  syncRaiseFields();
  var m = document.getElementById('reqModal');
  if (m) m.classList.add('open');
}
function closeRaise() {
  var m = document.getElementById('reqModal');
  if (m) m.classList.remove('open');
}
async function saveRaise() {
  var val = function (id) { var el = document.getElementById(id); return el ? String(el.value || '').trim() : ''; };
  var data = {
    staff_id: parseInt(val('reqStaff'), 10),
    leave_type: val('reqType'),
    start_date: val('reqDate'),
    end_date: val('reqEnd') || val('reqDate'),
    toil_hours: val('reqToilHours') || null,
    apl_reason: val('reqAplReason') || null,
    reason: val('reqReason') || null,
  };
  // 19 Oct 2026: the Leave Application Form's fields; the server checks them again (validateLeaveForm)
  var kind = data.leave_type === 'holiday' ? (val('reqKind') || 'annual') : null;
  var tick = document.getElementById('reqTicket');
  data.leave_kind = kind;
  data.ticket = kind === 'annual' && !!(tick && tick.checked);
  data.contact_local = val('reqLocal') || null;
  data.contact_home = val('reqHome') || null;
  data.property = PROPERTY_ITEMS.filter(function (p) { var el = document.getElementById('reqProp-' + p.key); return !!(el && el.checked); }).map(function (p) { return p.key; });
  data.property_other = val('reqPropOther') || null;
  if (!data.staff_id) { toast('Choose who the leave is for', 'error'); return; }
  if (!data.start_date) { toast('Choose the first day', 'error'); return; }
  if (!data.reason) { toast('Write the reason for the leave.', 'error'); return; }
  if (needsContacts(data.leave_type) && !data.contact_home) { toast('Add a home-town contact number for while you are on leave.', 'error'); return; }
  try {
    var res = await DB.leaveRequests.adminCreate(data);
    closeRaise();
    toast('Leave request ' + ((res && res.ticket) || '') + ' raised', 'success');
    var sel = document.getElementById('reqFilterStatus');
    if (sel) sel.value = 'pending';
    await render();
  } catch (e) { toast((e && e.message) || 'Could not raise the request', 'error'); }
}

function filterChanged() { paint(); }

// ── The email's link (19 Oct 2026): admin.html?tab=people&sub=requests&lr=<id> ──
// admin.html calls this after sign-in with the id (digits only). It opens People, Leave requests, shows every request
// (so the one asked for is on screen whatever the filter), scrolls to it and opens its day-by-day check. The server
// decides what this account may see: a request outside your stores is simply not in your list, and says so.
async function openLink(id) {
  var rid = String(id == null ? '' : id);
  if (!/^\d{1,12}$/.test(rid)) return false;
  try {
    if (typeof switchTab === 'function') {
      var btn = Array.prototype.slice.call(document.querySelectorAll('#mainTabs .tab')).find(function (el) { return (el.getAttribute('onclick') || '').indexOf("switchTab('people'") !== -1; });
      switchTab('people', btn || null);
    }
    if (typeof switchGroupSub === 'function') switchGroupSub('people', 'requests');
  } catch (e) { /* the page still renders below */ }
  _state.filterSet = true;
  var sel = document.getElementById('reqFilterStatus');
  if (sel) sel.value = '';
  await render();
  var found = _rows.some(function (r) { return String(r.id) === rid; });
  if (!found) { toast('That leave request is not in your list.', 'error'); return false; }
  var card = document.querySelector('[data-lr="' + rid + '"]');
  if (card && card.scrollIntoView) card.scrollIntoView({ behavior: 'smooth', block: 'start' });
  var row = _rows.filter(function (r) { return String(r.id) === rid; })[0];
  if (row && row.status === 'pending' && !(stageOf(row) === 'with_hr' && !_state.canApl)) await openPreview(Number(rid));
  return true;
}

if (typeof window !== 'undefined') {
  window.LeaveRequestsAdmin = { render, load, openPreview, toggleDay, toggleAck, approve, okRequest, openLink, startDecline, confirmDecline, cancelLeave, openRaise, closeRaise, saveRaise, syncRaiseFields, filterChanged, resendHr };
}
export { render, load, openPreview, toggleDay, toggleAck, approve, okRequest, openLink, startDecline, confirmDecline, cancelLeave, openRaise, closeRaise, saveRaise, syncRaiseFields, filterChanged, resendHr };
